import { Box, MenuItem, Select, TextField } from '@mui/material';
import React from 'react';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { useAuthStore } from '../../store/storeAuth';
import Label from '../UI/Label/Label';
import MicrosoftFileSelect from './MicrosoftFileSelect';
import { PushDataOption } from '../PushDataNode/PushDataNode';

interface PushMicrosoftWordDataProps {
  formData: any;
  onChange: (e: { target: { name: string; value: any } }) => void;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  nodeId: string;
  nodeLabel: string;
}

const PushMicrosoftWordData: React.FC<PushMicrosoftWordDataProps> = ({
  formData,
  onChange,
  onChangeSelect,
  data,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const fileId = data?.microsoftWordFileId ?? '';
  const selectedPushDataOption =
    formData.selectedPushDataOption ?? PushDataOption.END_OF_DOC;

  const specifiedPageChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (value !== '' && (isNaN(+value) || +value < 0)) {
      setNotification({
        type: NotificationType.Error,
        message: 'Page must be a valid positive number!',
      });
    }
    onChange(e);
  };

  const selectSx = {
    width: '100%',
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <MicrosoftFileSelect
        fileType="docx"
        fileIdField="microsoftWordFileId"
        label="documents"
        selectLabel="Search or select a Word document"
        onChangeSelect={onChangeSelect}
        data={data}
      />

      {!fileId && (
        <Box>
          <Label
            isAdvanced
            labelStyle={{
              fontSize: '14px',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              fontWeight: 400,
            }}
            labelName="New Document Name"
          />
          <TextField
            placeholder="Document Name"
            onChange={onChange}
            value={formData.newDocumentName ?? ''}
            name="newDocumentName"
            fullWidth
            InputProps={{
              style: {
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              },
            }}
            sx={{
              marginTop: '8px',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
              border: `2px solid ${
                user?.theme === 'dark' ? '#475467' : '#EAECF0'
              }`,
            }}
          />
        </Box>
      )}

      {fileId && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Label
            labelStyle={{
              fontSize: '14px',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              fontWeight: 400,
            }}
            labelName="Option"
            isAdvanced
          />
          <Select
            value={selectedPushDataOption}
            onChange={e =>
              onChangeSelect({
                target: {
                  name: 'selectedPushDataOption',
                  value: e.target.value as PushDataOption,
                },
              })
            }
            className="nodrag"
            sx={{ ...selectSx, marginTop: 0 }}
            MenuProps={menuPropsSx}
          >
            {Object.values(PushDataOption).map(opt => (
              <MenuItem
                key={opt}
                value={opt}
                sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black' }}
              >
                {opt}
              </MenuItem>
            ))}
          </Select>

          {selectedPushDataOption ===
            PushDataOption.NEW_PAGE_AFTER_SPECIFIED_PAGE && (
            <Box sx={{ mt: '4px' }}>
              <Label labelName="Page Number" isAdvanced />
              <TextField
                type="number"
                placeholder="Enter Page"
                name="specifiedPage"
                value={formData.specifiedPage ?? ''}
                onChange={specifiedPageChangeHandler}
                fullWidth
                InputProps={{
                  style: {
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  },
                }}
                sx={{
                  marginTop: '8px',
                  bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  border: `2px solid ${
                    user?.theme === 'dark' ? '#475467' : '#EAECF0'
                  }`,
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PushMicrosoftWordData;
